import {usePortalTooltip} from '@github-ui/portal-tooltip/use-portal-tooltip'
import {testIdProps} from '@github-ui/test-id-props'
import {Button} from '@primer/react'
import {clsx} from 'clsx'
import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  differenceInMonths,
  differenceInWeeks,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import {memo, type ReactNode, useMemo, useRef} from 'react'

import {RoadmapZoomLevel} from '../../../api/view/contracts'
import useIsVisible from '../../../components/board/hooks/use-is-visible'
import {dateStringFromISODate} from '../../../helpers/date-string-from-iso-string'
import {intervalDateRange} from '../../../helpers/iterations'
import {getIterationOptionsTooltipText} from '../../../helpers/roadmap-helpers'
import {useRoadmapTableWidth, useRoadmapZoomLevel} from '../../../hooks/use-roadmap-settings'
import {
  isCurrentIterationByMarker,
  type RoadmapMarker,
  RoadmapMarkerType,
  useRoadmapMarkersInRange,
} from '../components/roadmap-markers-in-range'
import {startOfDayUtc} from '../date-utils'
import {useRoadmapView} from '../roadmap-view-provider'
import CustomDateMarkerSvg from '../svgs/caret-down'
import MilestoneMarkerSvg from '../svgs/diamond'
import IterationMarkerSvg from '../svgs/dot-empty'
import TodayMarkerSvg from '../svgs/dot-fill'
import {getTooltipText} from '../tooltip-helper'
import styles from './roadmap-marker.module.css'

const MARKER_NUB_SIZE = 7

/**
 * Renders a link to skip the markers section of the roadmap.
 */
export function SkipMarkerLink({toEnd = true}) {
  const tableWidth = useRoadmapTableWidth()
  // used to bypass the markers section of the roadmap
  return (
    <a
      href={toEnd ? '#end-of-markers' : '#start-of-markers'}
      className={`p-3 color-bg-accent-emphasis color-fg-on-emphasis ${styles.roadmapShowOnFocus}`}
      style={{left: `${tableWidth}px`}}
    >
      Skip to {toEnd ? 'end' : 'start'} of markers
    </a>
  )
}

/**
 * Renders the small circles (or other shapes) at the top of a marker, at the bottom edge of the header
 * Currently includes the "Today" marker, but could be expanded to include other markers like "Iteration X".
 * */
export function RoadmapMarkerHeaderNubs() {
  return (
    <div role="row" className={styles.Box} id="start-of-markers">
      <div style={{position: 'absolute', width: '100%'}}>
        <SkipMarkerLink />
      </div>
      <TodayMarkerNub />
      <MarkerNubs />
      <SkipMarkerLink toEnd={false} />
    </div>
  )
}

/**
 * Renders the vertical marker lines within the roadmap item area
 * Currently includes the "Today" marker and roadmap date dividers, but could be expanded to include other markers like "Iteration X".
 * */
export function RoadmapMarkers() {
  return (
    <>
      <DividerMarkersLines />
      <TodayMarkerLine />
      <MarkerLines />
    </>
  )
}

function TodayMarkerNub() {
  const {getX, today} = useRoadmapView()
  const {singleDayMarkerMap} = useRoadmapMarkersInRange()

  const midday = useMemo(() => addHours(today, 12), [today])
  const leftOffset = getX(midday) - 3.5

  const ref = useRef<HTMLButtonElement | null>(null)
  const {isVisible} = useIsVisible({ref})
  const [contentProps, tooltip, toggleTooltip] = usePortalTooltip({
    contentRef: ref,
    'aria-label': `Today: ${getTooltipText({start: today, end: today})}`,
  })

  const todayDateString = dateStringFromISODate(today.toISOString())
  // if a single day marker is already present, don't render the today marker
  const isSingleDateMarkerPresent = singleDayMarkerMap.has(todayDateString)

  return (
    <div role="gridcell">
      <Button
        variant="invisible"
        disabled={!isVisible}
        onClick={toggleTooltip}
        ref={ref}
        style={{left: `${leftOffset}px`}}
        className={clsx(
          styles.markerNubBase,
          styles.roadmapNub,
          isVisible && !isSingleDateMarkerPresent ? undefined : styles.invisibleNub,
        )}
        {...contentProps}
        {...testIdProps(`roadmap-today-marker-nub`)}
      >
        {tooltip}

        <TodayMarkerSvg fill={'var(--roadmap-today-nub-color)'} width={MARKER_NUB_SIZE + 1} />
      </Button>
    </div>
  )
}

function TodayMarkerLine() {
  const {getX, today, totalWidth} = useRoadmapView()
  const {singleDayMarkerMap} = useRoadmapMarkersInRange()

  const midday = useMemo(() => addHours(today, 12), [today])
  const leftOffset = getX(midday)
  const isInTimeRange = leftOffset > 0 && leftOffset < totalWidth

  if (!isInTimeRange) return null

  const todayDateString = dateStringFromISODate(today.toISOString())
  // if a single day marker is already present, don't render the today marker
  if (singleDayMarkerMap.has(todayDateString)) return null

  return (
    <div
      // Slightly move marker to the left to accomodate thicker border on today marker
      style={{left: `${leftOffset - 0.5}px`, height: '100%'}}
      className={styles.Box_1}
      {...testIdProps(`roadmap-today-marker`)}
    />
  )
}
function MarkerNubs() {
  const {markersInRange} = useRoadmapMarkersInRange()

  return (
    <>
      {markersInRange.map(marker => (
        <MarkerNub marker={marker} key={marker.id} isCurrent={isCurrentIterationByMarker(marker)} />
      ))}
    </>
  )
}

const markerNubStyleMap = {
  [RoadmapMarkerType.CustomDate]: clsx(styles.markerNubBase),
  [RoadmapMarkerType.Iteration]: clsx(styles.markerNubBase),
  [RoadmapMarkerType.IterationBreak]: clsx(styles.markerNubBase),
  [RoadmapMarkerType.Milestone]: clsx(styles.markerNubBase),
}

function MarkerNub({isCurrent = false, marker}: {isCurrent?: boolean; marker: RoadmapMarker}) {
  const {getX, today} = useRoadmapView()

  const {startDate, endDate} = useMemo(() => {
    const markerStartDate = 'duration' in marker ? new Date(marker.date) : addHours(new Date(marker.date), 12)
    const markerEndDate = 'duration' in marker ? addDays(markerStartDate, marker.duration) : new Date(marker.date)
    return {
      startDate: markerStartDate,
      endDate: markerEndDate,
    }
  }, [marker])

  const leftOffset = getX(new Date(startDate)) - 3
  const ref = useRef<HTMLButtonElement | null>(null)
  const {isVisible} = useIsVisible({ref})
  const todayDateString = dateStringFromISODate(today.toISOString())
  // is today and single day marker
  const isToday = marker.date === todayDateString && !('duration' in marker)

  const markerSvgMap = useMemo<Record<string, ReactNode>>(
    () => ({
      [RoadmapMarkerType.IterationBreak]: (
        <MilestoneMarkerSvg fill={`var(--roadmap-iteration-break-nub-color)`} width={MARKER_NUB_SIZE + 1} />
      ),
      [RoadmapMarkerType.Iteration]: (
        <IterationMarkerSvg
          fill={isCurrent ? `var(--roadmap-current-iteration-nub-color)` : `var(--roadmap-iteration-nub-color)`}
          width={MARKER_NUB_SIZE + 1}
        />
      ),
      [RoadmapMarkerType.Milestone]: (
        <MilestoneMarkerSvg fill={`var(--roadmap-milestone-nub-color)`} width={MARKER_NUB_SIZE + 1} />
      ),
      [RoadmapMarkerType.CustomDate]: (
        <CustomDateMarkerSvg
          fill={isToday ? `var(--roadmap-today-nub-color)` : `var(--roadmap-custom-date-nub-color)`}
          width={MARKER_NUB_SIZE + 1}
        />
      ),
    }),
    [isToday, isCurrent],
  )

  const {startDate: tooltipStart, endDate: tooltipEnd} =
    'duration' in marker
      ? intervalDateRange({
          startDate: marker.date,
          duration: marker.duration,
        })
      : {startDate, endDate}

  const tooltipText =
    'duration' in marker && marker.options.length > 1
      ? getIterationOptionsTooltipText(marker.options)
      : `${marker.title}: ${getTooltipText({
          start: tooltipStart,
          end: tooltipEnd,
        })}`

  const [contentProps, tooltip, toggleTooltip] = usePortalTooltip({
    contentRef: ref,
    'aria-label': tooltipText,
  })

  const className = markerNubStyleMap[marker.type]

  return (
    <div role="gridcell">
      <Button
        variant="invisible"
        disabled={!isVisible}
        onClick={toggleTooltip}
        ref={ref}
        className={clsx(styles.roadmapNub, className)}
        style={{
          left: `${leftOffset}px`,
          ...(isVisible ? {} : {backgroundColor: 'transparent', borderColor: 'transparent'}),
        }}
        {...contentProps}
        {...testIdProps(`${marker.type}-marker-nub-${marker.id}`)}
      >
        {tooltip}
        {markerSvgMap[marker.type]}
      </Button>
    </div>
  )
}

const MarkerLines = memo(function MarkerLines() {
  const {markersInRange} = useRoadmapMarkersInRange()

  return (
    <>
      {markersInRange.map(marker => (
        <MarkerLine key={marker.id} marker={marker} isCurrent={isCurrentIterationByMarker(marker)} />
      ))}
    </>
  )
})

const markerLineStyleMap = {
  [RoadmapMarkerType.CustomDate]: clsx(styles.markerLineBase, styles.customDateLine),
  [RoadmapMarkerType.Iteration]: clsx(styles.markerLineBase, styles.iterationLine),
  [RoadmapMarkerType.IterationBreak]: clsx(styles.markerLineBase, styles.iterationLine),
  [RoadmapMarkerType.Milestone]: clsx(styles.markerLineBase, styles.milestoneLine),
}

function MarkerLine({marker, isCurrent = false}: {marker: RoadmapMarker; isCurrent?: boolean}) {
  const {getX, today} = useRoadmapView()

  const date = useMemo(
    () => ('duration' in marker ? new Date(marker.date) : addHours(new Date(marker.date), 12)),
    [marker],
  )
  const leftOffset = getX(new Date(date))
  const todayDateString = dateStringFromISODate(today.toISOString())
  // is today and single day marker
  const isToday = marker.date === todayDateString && !('duration' in marker)
  const className = isCurrent
    ? clsx(styles.markerLineBase, styles.iterationLine, styles.currentIterationLine)
    : markerLineStyleMap[marker.type]

  return (
    <div
      {...testIdProps(`${marker.type}-marker-line`)}
      className={clsx(className, isToday && styles.markerLineToday)}
      style={{left: `${leftOffset}px`, height: `100%`}}
    />
  )
}

/** Renders subtle marker lines (no nubs) that are used to divide the roadmap area based on month/week starts */
function DividerMarkersLines() {
  const {getX, timeRangeEnd, timeRangeStart} = useRoadmapView()
  const zoomLevel = useRoadmapZoomLevel()

  const totalMarkers =
    zoomLevel === RoadmapZoomLevel.Month
      ? Math.abs(differenceInWeeks(timeRangeEnd, timeRangeStart)) + 1
      : Math.abs(differenceInMonths(timeRangeEnd, timeRangeStart)) + 1

  const dates = Array.from({length: totalMarkers}, (_, i) => {
    // Use Monday as the start of the week in the month view
    return zoomLevel === RoadmapZoomLevel.Month
      ? startOfDayUtc(addWeeks(startOfWeek(timeRangeStart, {weekStartsOn: 1}), i))
      : startOfDayUtc(addMonths(startOfMonth(timeRangeStart), i))
  })

  return (
    <>
      {dates.map(date => {
        const leftOffset = getX(date)
        return (
          <div
            key={leftOffset}
            style={{left: `${leftOffset}px`, height: `100%`}}
            className={clsx(styles.markerLineBase, styles.dividerLine)}
          />
        )
      })}
    </>
  )
}
